/**
 * Tiny WebAudio sound-design helper. No audio files, no autoplay:
 * the context is created lazily on the first user-triggered sound.
 */
let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  try {
    ctx ??= new AudioContext();
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

type ToneOptions = {
  freq?: number;
  to?: number;
  duration?: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
};

export function tone({
  freq = 620,
  to,
  duration = 0.08,
  type = "triangle",
  gain = 0.08,
  delay = 0,
}: ToneOptions = {}) {
  const ac = audio();
  if (!ac) return;
  const t0 = ac.currentTime + delay;
  const osc = ac.createOscillator();
  const amp = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (to && to !== freq)
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t0 + duration);
  amp.gain.setValueAtTime(0.0001, t0);
  amp.gain.exponentialRampToValueAtTime(gain, t0 + Math.min(0.02, duration / 3));
  amp.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(amp).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}

function noise(duration = 0.25, gain = 0.05, sweep: [number, number] = [900, 300]) {
  const ac = audio();
  if (!ac) return;
  const frames = Math.floor(ac.sampleRate * duration);
  const buffer = ac.createBuffer(1, frames, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
  const src = ac.createBufferSource();
  src.buffer = buffer;
  const filter = ac.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(sweep[0], ac.currentTime);
  filter.frequency.exponentialRampToValueAtTime(sweep[1], ac.currentTime + duration);
  const amp = ac.createGain();
  amp.gain.setValueAtTime(gain, ac.currentTime);
  amp.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + duration);
  src.connect(filter).connect(amp).connect(ac.destination);
  src.start();
}

/** Wheel/roulette peg click. `intensity` 0..1 controls brightness. */
export function sfxTick(intensity = 1) {
  tone({
    freq: 1200 + intensity * 700,
    to: 480,
    duration: 0.035,
    type: "square",
    gain: 0.018 + intensity * 0.03,
  });
}

/** Soft UI click for buttons and shuffles. */
export function sfxClick() {
  tone({ freq: 520, to: 760, duration: 0.05, type: "triangle", gain: 0.05 });
}

/** Air movement when something starts spinning or rolling. */
export function sfxWhoosh() {
  noise(0.45, 0.05, [1400, 260]);
}

/** Short celebratory arpeggio for a result reveal. */
export function sfxFanfare() {
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((freq, i) =>
    tone({ freq, duration: 0.32, type: "sine", gain: 0.07, delay: i * 0.075 }),
  );
  tone({ freq: 261.63, duration: 0.5, type: "triangle", gain: 0.05, delay: 0.06 });
}

/** Countdown beep / timer end. */
export function sfxBeep(final = false) {
  if (final) {
    [880, 880, 1174].forEach((f, i) =>
      tone({ freq: f, duration: 0.18, type: "square", gain: 0.06, delay: i * 0.2 }),
    );
  } else {
    tone({ freq: 720, duration: 0.1, type: "sine", gain: 0.05 });
  }
}

/** Two-note cue when the timer starts or resumes. */
export function sfxTimerStart() {
  tone({ freq: 523.25, duration: 0.12, type: "sine", gain: 0.05 });
  tone({ freq: 784, duration: 0.18, type: "sine", gain: 0.05, delay: 0.1 });
}

/** Descending cue when the timer is paused. */
export function sfxTimerPause() {
  tone({ freq: 660, to: 392, duration: 0.16, type: "triangle", gain: 0.045 });
}

/** Neutral cue for reset / duration change. */
export function sfxTimerReset() {
  tone({ freq: 392, to: 523.25, duration: 0.14, type: "triangle", gain: 0.04 });
}

/**
 * Milestone chime, e.g. halfway or "one minute left".
 * `weight` 0..1 makes later milestones brighter and more insistent.
 */
export function sfxTimerMilestone(weight = 0.5) {
  const base = 587.33 + weight * 180;
  tone({ freq: base, duration: 0.2, type: "sine", gain: 0.045 + weight * 0.02 });
  tone({
    freq: base * 1.5,
    duration: 0.26,
    type: "sine",
    gain: 0.035 + weight * 0.02,
    delay: 0.13,
  });
}

/**
 * Final-seconds tick. `progress` 0..1 runs from the first tick to the last,
 * raising pitch and volume so the urgency is audible without watching the clock.
 */
export function sfxTimerTick(progress = 0) {
  const p = Math.min(1, Math.max(0, progress));
  tone({
    freq: 640 + p * 420,
    to: 420 + p * 260,
    duration: 0.06 + p * 0.03,
    type: "square",
    gain: 0.03 + p * 0.035,
  });
}

/** Layered alarm for time-up: three chord hits plus a soft sweep. */
export function sfxTimerEnd() {
  const chord = [659.25, 880, 1108.73];
  [0, 0.26, 0.52].forEach((delay, hit) => {
    chord.forEach((freq) =>
      tone({
        freq,
        duration: 0.24,
        type: hit === 2 ? "square" : "sine",
        gain: 0.045,
        delay,
      }),
    );
  });
  tone({ freq: 329.63, duration: 0.9, type: "triangle", gain: 0.04, delay: 0.05 });
  noise(0.5, 0.03, [2200, 500]);
}

/** Rapid rattle of dice colliding in a cup or tray. */
export function sfxDiceRattle() {
  for (let i = 0; i < 7; i++) {
    const delay = i * 0.055 + Math.random() * 0.02;
    const freq = 650 + Math.random() * 850;
    tone({ freq, to: freq - 220, duration: 0.035, type: "triangle", gain: 0.035, delay });
  }
  noise(0.25, 0.025, [1300, 350]);
}

/** Solid thud when dice land on the felt table. */
export function sfxDiceLand() {
  tone({ freq: 170, to: 70, duration: 0.09, type: "sine", gain: 0.07 });
  tone({ freq: 340, to: 110, duration: 0.06, type: "triangle", gain: 0.04, delay: 0.01 });
}

